import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { CUSTOM, UNITS_TABLE_DATA, UNITCONSTANT, UNITVALUECONSTANT } from '../constants/unit-constant';
import { Units, UnitsData, UnitsGetData, UserSettingPost } from '../models/unit';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { ApiConstants } from '../constants/api-constant';
import { EmailSettingHistory, EmailSettingTable } from '../models/emailsetting';

@Injectable({
  providedIn: 'root',
})
export class UserSettingsService {
  public unitSelected$: Observable<string>;
  public unitSelected = new BehaviorSubject<string | ''>(sessionStorage.getItem('unit') ?? '');
  private baseUrl: string;
  private url = ApiConstants.BASEURL;

  constructor(private http: HttpClient) {
    this.unitSelected$ = this.unitSelected.asObservable();
    this.baseUrl = environment.apiBaseUrl;
  }

  public getLatestUnits(): Observable<UnitsGetData> {
    return this.http.get<UnitsGetData>(`${this.baseUrl}${ApiConstants.BASEURL}${ApiConstants.USERSETTING}get-user-settings/unit`);
  }

  public getEmailSetting(): Observable<EmailSettingHistory[]> {
    return this.http.get<EmailSettingHistory[]>(`${this.baseUrl}${ApiConstants.BASEURL}${ApiConstants.USERSETTING}get-user-email-settings`);
  }

  public saveEmailSetting(setting: EmailSettingTable[]): Observable<EmailSettingTable[]> {
    return this.http.post<EmailSettingTable[]>(`${this.baseUrl}${this.url}${ApiConstants.USERSETTING}add-user-email-settings`, setting);
  }

  public saveLatestUnits(unit: UnitsData): Observable<UserSettingPost> {
    const constructUnit = {
      unitName: unit.unitName,
      unitData: unit.unitData,
    };
    const data = {
      referenceType: 'user',
      preference: 1,
      settingType: 'unit',
      settingProperties: constructUnit,
    };

    return this.http.post<UserSettingPost>(`${this.baseUrl}${this.url}${ApiConstants.USERSETTING}add-user-settings`, data);
  }

  public setUnit(unitData: string): void {
    this.unitSelected.next(unitData);
  }

  public createFinalUnits(selectedColumn: string, timeStamp: string): UnitsData {
    const units = JSON.parse(JSON.stringify(UNITS_TABLE_DATA));
    const finalArray: { [key: string]: string }[] = units.map((tableData: Units) => {
      return {
        [tableData.unitName]: tableData[selectedColumn],
      };
    });

    const unit: UnitsData = {
      unitName: selectedColumn,
      unitData: finalArray,
      timeStamp,
    };

    return unit;
  }

  public createCustomUnits(units: { [key: string]: string }[], timeStamp: string): UnitsData {
    const unit: UnitsData = {
      unitName: CUSTOM,
      unitData: units,
      timeStamp,
    };

    return unit;
  }

  public siToChosenUnit(value: number, fromUnit: string, toUnit: string): string {
    let calculatedValue = '';
    if (typeof value !== 'undefined') {
      fromUnit = fromUnit.replace(/\(([^)]+)\)/g, '$1');
      toUnit = toUnit.replace(/\(([^)]+)\)/g, '$1');
      if (
        (fromUnit === UNITCONSTANT.METER && toUnit === UNITCONSTANT.FEET) ||
        (fromUnit === UNITCONSTANT.MERTERPERSEC && toUnit === UNITCONSTANT.FEETPERSEC)
      ) {
        calculatedValue = this.checkDecimal(value * UNITVALUECONSTANT.METERTOFEET);
      } else if (fromUnit === UNITCONSTANT.PASCAL && toUnit === UNITCONSTANT.KILOPASCAL) {
        calculatedValue = this.checkDecimal(value * UNITVALUECONSTANT.UNITTOKILO);
      } else if (fromUnit === UNITCONSTANT.PASCAL && toUnit === UNITCONSTANT.PSI) {
        calculatedValue = this.checkDecimal(value * UNITVALUECONSTANT.PASCALTOPSI);
      } else if (fromUnit === UNITCONSTANT.METERCUBEPERSEC && toUnit === UNITCONSTANT.LITREPERMIN) {
        calculatedValue = this.checkDecimal(value * UNITVALUECONSTANT.M3PERSECTOLPERMIN);
      } else if (fromUnit === UNITCONSTANT.METERCUBEPERSEC && toUnit === UNITCONSTANT.GALOONSPERMIN) {
        calculatedValue = this.checkDecimal(value * UNITVALUECONSTANT.M3PERSECTOGPERMIN);
      } else if (fromUnit === UNITCONSTANT.NEWTON && toUnit === UNITCONSTANT.MILLIGRAVEFORCE) {
        calculatedValue = this.checkDecimal(value * UNITVALUECONSTANT.NEWTOMGF);
      } else if (fromUnit === UNITCONSTANT.NEWTON && toUnit === UNITCONSTANT.KIPFORCE) {
        calculatedValue = this.checkDecimal(value * UNITVALUECONSTANT.NEWTOKLPF);
      } else if (fromUnit === UNITCONSTANT.NEWTONMETER && toUnit === UNITCONSTANT.KILONEWTONMETER) {
        calculatedValue = this.checkDecimal(value * UNITVALUECONSTANT.UNITTOKILO);
      } else if (fromUnit === UNITCONSTANT.NEWTONMETER && toUnit === UNITCONSTANT.KIPFORCEPERFEET) {
        calculatedValue = this.checkDecimal(value * UNITVALUECONSTANT.NMTOKLPFFT);
      } else if (fromUnit === UNITCONSTANT.MERTERPERSEC && toUnit === UNITCONSTANT.FEETPERMIN) {
        calculatedValue = this.checkDecimal(value * UNITVALUECONSTANT.METERSECTOFEETMIN);
      } else if (fromUnit === UNITCONSTANT.MERTERPERSEC && toUnit === UNITCONSTANT.FEETPERHR) {
        calculatedValue = this.checkDecimal(value * UNITVALUECONSTANT.METERSECTOFEETHR);
      } else {
        calculatedValue = this.checkDecimal(value);
      }
    }

    return calculatedValue;
  }

  public conversionToSIUnits(value: number, unit: string): number {
    unit = unit.replace(/\(([^)]+)\)/g, '$1');
    let calculatedValue = 0;
    switch (unit) {
      case UNITCONSTANT.FEET:
        calculatedValue = Number(this.checkDecimal(value / UNITVALUECONSTANT.METERTOFEET));
        break;
      case UNITCONSTANT.FEETPERSEC:
        calculatedValue = Number(this.checkDecimal(value / UNITVALUECONSTANT.METERTOFEET));
        break;
      case UNITCONSTANT.PSI:
        calculatedValue = Number(this.checkDecimal(value / UNITVALUECONSTANT.PASCALTOPSI));
        break;
      case UNITCONSTANT.KILOPASCAL:
        calculatedValue = Number(this.checkDecimal(value / UNITVALUECONSTANT.UNITTOKILO));
        break;
      case UNITCONSTANT.LITREPERMIN:
        calculatedValue = Number(this.checkDecimal(value / UNITVALUECONSTANT.M3PERSECTOLPERMIN));
        break;
      case UNITCONSTANT.GALOONSPERMIN:
        calculatedValue = Number(this.checkDecimal(value / UNITVALUECONSTANT.M3PERSECTOGPERMIN));
        break;
      case UNITCONSTANT.MILLIGRAVEFORCE:
        calculatedValue = Number(this.checkDecimal(value / UNITVALUECONSTANT.NEWTOMGF));
        break;
      case UNITCONSTANT.KIPFORCE:
        calculatedValue = Number(this.checkDecimal(value / UNITVALUECONSTANT.NEWTOKLPF));
        break;
      case UNITCONSTANT.KILONEWTONMETER:
        calculatedValue = Number(this.checkDecimal(value / UNITVALUECONSTANT.UNITTOKILO));
        break;
      case UNITCONSTANT.KIPFORCEPERFEET:
        calculatedValue = Number(this.checkDecimal(value / UNITVALUECONSTANT.NMTOKLPFFT));
        break;
      case UNITCONSTANT.FEETPERMIN:
        calculatedValue = Number(this.checkDecimal(value / UNITVALUECONSTANT.METERSECTOFEETMIN));
        break;
      case UNITCONSTANT.FEETPERHR:
        calculatedValue = Number(this.checkDecimal(value / UNITVALUECONSTANT.METERSECTOFEETHR));
        break;
      default:
        calculatedValue = Number(this.checkDecimal(value));
        break;
    }

    return calculatedValue;
  }

  public getUnit(si: string, unitSelected: { [key: string]: string }[]): string {
    let chosenUnit: string = si;
    unitSelected.forEach(unit => {
      const unitName = Object.keys(unit)[0];
      const unitValue = unit[unitName];
      const validUnit = si.replace(/\(([^)]+)\)/g, '$1');
      const formData = UNITS_TABLE_DATA.find(d => d.unitName === unitName && d.si === validUnit);
      if (formData) {
        chosenUnit = '(' + unitValue + ')';
      }
    });

    return chosenUnit;
  }

  private checkDecimal(value: number): string {
    if (value !== null) {
      value = typeof value === 'string' ? parseFloat(value) : value;
      const splitDecimal = value?.toString()?.split('.');
      let strValue = value?.toString();
      if (splitDecimal?.[1]?.length > 7) {
        strValue = value.toFixed(7);
      } else if (splitDecimal?.[1]?.length > 0) {
        strValue = value.toFixed(splitDecimal?.[1]?.length);
      } else {
        strValue = value.toFixed(1);
      }

      return strValue;
    }

    return value;
  }
}
